import React, { useMemo, useCallback, useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Accordion,
  Table as TableComponent,
  Button,
  Badge,
  Loader,
  StatusBanner,
} from '@lucid/lego';
import type {
  OrderDelivery,
  DeliveryLineItem,
  DeliveryStatus,
} from '../../../../api/services/lupp/order/importer/dto.importer';
import type { ModuleFileReference } from '../../../../api/services/lupp/order/dto.order';
import {
  useGetOrderDeliveries,
  useUpdateDeliveryStatus,
  useSearchImporterOrders,
} from '../../../../api/services/lupp/order/importer/importer';
import { downloadSfdcFileFromFilehub } from '../../../../api/services/lupp/fileUpload/fileUpload';
import { formatDate, formatVehicleModelName } from '../../../../utils/tools';
import ConfirmPickupSlider from './ConfirmPickupSlider';
import styles from './Deliveries.module.scss';

const SUCCESS_BANNER_TIMEOUT_MS = 5000;

type DeliveriesProps = {
  theme: 'light' | 'dark';
  labels: Record<string, string>;
  importerOrderId: string | null;
  importerOrderNumber?: string;
  onBack?: () => void;
  fileReferences?: ModuleFileReference[];
};

const getDeliveryStatusBadgeColor = (status: DeliveryStatus) => {
  switch (status) {
    case 'READY_FOR_DISPATCH':
      return 'teal';
    case 'READY_FOR_PICKUP':
      return 'teal';
    case 'COMMERCIAL_INVOICE_GENERATED':
      return 'blue';
    case 'PICKUP_CONFIRMED':
      return 'green';
    case 'CANCELLED':
      return 'red';
    default:
      return 'gray';
  }
};

/** i18n keys for statuses whose label differs from their humanized code. */
const STATUS_LABEL_KEYS: Record<string, { key: string; fallback: string }> = {
  READY_FOR_DISPATCH: { key: 'status.readyForDispatch', fallback: 'Ready for Pickup' },
  READY_FOR_PICKUP: { key: 'status.readyForDispatch', fallback: 'Ready for Pickup' },
};

const formatStatusLabel = (status: string, labels: Record<string, string>) => {
  const override = STATUS_LABEL_KEYS[status.replace(/ /g, '_').toUpperCase()];
  if (override) return labels[override.key] ?? override.fallback;

  return status
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

/**
 * Deliveries view for the Order Desk page.
 * Displays deliveries grouped by delivery number in accordions,
 * each containing a table of line items and CTA buttons.
 * @component
 * @param {DeliveriesProps} props - Component props
 * @returns {JSX.Element}
 * @example
 * ```tsx
 * <Deliveries
 *   theme="dark"
 *   labels={labels}
 *   importerOrderId="189eb795-..."
 *   importerOrderNumber="IO-AHRHXEUQ"
 *   onBack={handleBack}
 * />
 * ```
 */
const Deliveries: React.FC<DeliveriesProps> = ({
  theme,
  labels,
  importerOrderId,
  importerOrderNumber,
  onBack,
  fileReferences = [],
}) => {
  const invertedTheme = theme === 'light' ? 'dark' : 'light';

  const [pickupSliderDelivery, setPickupSliderDelivery] = useState<OrderDelivery | null>(null);
  const [successMessage, setSuccessMessage] = useState('');

  // Auto-dismiss the success banner after a short delay.
  useEffect(() => {
    if (!successMessage) return;
    const timer = setTimeout(() => setSuccessMessage(''), SUCCESS_BANNER_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [successMessage]);

  // Fallback: resolve importerOrderId from order number when not in the paginated list
  // (e.g. direct URL navigation to /deliveries without going through the orders table first)
  const { data: fallbackOrderData, isLoading: fallbackOrderLoading } = useSearchImporterOrders({
    searchString: importerOrderNumber,
    size: 1,
    enabled: !importerOrderId && Boolean(importerOrderNumber),
  });

  const resolvedOrderId = importerOrderId ?? fallbackOrderData?.items?.[0]?.importerOrderId ?? null;

  const {
    data: deliveries,
    isLoading,
    error,
  } = useGetOrderDeliveries({
    orderId: resolvedOrderId,
    enabled: !!resolvedOrderId,
  });

  const { isPending: isUpdating } = useUpdateDeliveryStatus();

  const handleConfirmPickup = useCallback((delivery: OrderDelivery) => {
    setPickupSliderDelivery(delivery);
  }, []);

  // The deliveries list refreshes automatically via React Query invalidation in
  // useConfirmDeliveryPickup; this surfaces a success banner to the user.
  const handlePickupSuccess = useCallback(() => {
    setSuccessMessage(
      labels?.['deliveries.pickupConfirmedSuccess'] ??
        'Pickup confirmed successfully. The delivery has been updated.'
    );
  }, [labels]);

  const [downloadingFileId, setDownloadingFileId] = useState<string | null>(null);

  const getDeliveryInvoices = useCallback(
    (delivery: OrderDelivery) => {
      if (!delivery.deliveryNumber) return [];
      const deliveryLevelRefs = delivery.moduleFileReferenceDtoList ?? [];
      const invoiceRefs =
        deliveryLevelRefs.length > 0
          ? deliveryLevelRefs
          : fileReferences.filter((f) => f.sfdcDeliveryIdentifier === delivery.deliveryNumber);

      return invoiceRefs.filter(
        (f) => f.documentCategory === 'COMMERCIAL_INVOICE' || f.documentCategory === 'FINAL_INVOICE'
      );
    },
    [fileReferences]
  );

  const handleDownloadInvoice = useCallback(
    async (delivery: OrderDelivery, file: ModuleFileReference) => {
      if (!delivery.deliveryNumber || !file.sfdcFileIdentifier) return;
      setDownloadingFileId(file.sfdcFileIdentifier);
      try {
        await downloadSfdcFileFromFilehub(
          delivery.deliveryNumber,
          file.sfdcFileIdentifier,
          file.fileNameOriginal ?? 'invoice.pdf'
        );
      } catch (error) {
        console.error('Failed to download invoice:', error);
      } finally {
        setDownloadingFileId(null);
      }
    },
    []
  );

  const columns = useMemo(() => {
    const renderCell = (value: string | null | undefined) => (
      <Typography variant="body" size="small" theme={invertedTheme} color="white">
        {value ?? '-'}
      </Typography>
    );

    return [
      {
        key: 'vehicleOrderNumber',
        title: labels?.['deliveries.table.orderNumber'] ?? 'Order Line #',
        width: '200px',
        sortable: true,
        render: (_: unknown, record: DeliveryLineItem) => renderCell(record.vehicleOrderNumber),
      },
      {
        key: 'vin',
        title: labels?.['deliveries.table.vin'] ?? 'VIN',
        width: '200px',
        sortable: true,
        render: (_: unknown, record: DeliveryLineItem) => renderCell(record.vin),
      },
      {
        key: 'model',
        title: labels?.['deliveries.table.model'] ?? 'Model',
        width: '150px',
        sortable: true,
        render: (_: unknown, record: DeliveryLineItem) =>
          renderCell(formatVehicleModelName(record.model)),
      },
      {
        key: 'vehicleStatus',
        title: labels?.['deliveries.table.vehicleStatus'] ?? 'Vehicle Status',
        width: '180px',
        sortable: true,
        render: (_: unknown, record: DeliveryLineItem) =>
          record.vehicleStatus ? (
            <Badge
              label={formatStatusLabel(record.vehicleStatus, labels)}
              type="secondary"
              color="gray"
              theme={invertedTheme}
            />
          ) : (
            renderCell(null)
          ),
      },
    ];
  }, [labels, invertedTheme]);

  const renderAccordionTitle = (delivery: OrderDelivery) => (
    <Box className={styles.accordionHeader}>
      <Box className={styles.accordionHeaderInfo}>
        <Typography variant="subtitle" size="small" color="white" theme="dark">
          {delivery.deliveryNumber}
        </Typography>
        <Typography variant="body" size="small" color="gray-5" theme="dark">
          {`Last updated ${formatDate(delivery.updatedAt)}`}
        </Typography>
      </Box>
      <Box className={styles.accordionHeaderRight}>
        <Typography variant="body" size="small" color="white" theme="dark">
          {`${delivery.lineItems.length} ${labels?.['deliveries.vehicles'] ?? 'Vehicles'}`}
        </Typography>
        <Badge
          label={formatStatusLabel(delivery.status, labels)}
          type="primary"
          color={getDeliveryStatusBadgeColor(delivery.status)}
          theme="light"
        />
      </Box>
    </Box>
  );

  if (fallbackOrderLoading || isLoading) {
    return (
      <Box data-testid="deliveries-view">
        <Box className={styles.loaderContainer}>
          <Loader size={48} theme={theme} />
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box data-testid="deliveries-view">
        <Typography variant="body" size="large" theme={theme} color="error-1">
          {labels?.['deliveries.error'] ?? 'Error loading deliveries. Please try again.'}
        </Typography>
      </Box>
    );
  }

  const activeDeliveries = (deliveries ?? []).filter((d) => d.status !== 'CANCELLED');

  return (
    <Box data-testid="deliveries-view">
      <Box mb={24}>
        <Box className={styles.pageHeader}>
          <Typography variant="headline" size="small" theme={theme} color="primary-1">
            {labels?.['deliveries.title'] ?? 'Deliveries'}
          </Typography>
        </Box>
        <Box pt={8} pb={12}>
          <Typography variant="body" size="medium" theme={theme} color="gray-3">
            {(
              labels?.['deliveries.subtitle'] ??
              `View all deliveries for order ${importerOrderNumber ?? ''}.`
            ).replace('{{orderNumber}}', importerOrderNumber ?? '')}
          </Typography>
        </Box>
        {onBack && (
          <Button
            type="tertiary"
            color="neutral"
            theme={theme}
            label={(
              labels?.['page.backToLineItems'] ??
              `Back to order ${importerOrderNumber ?? 'Line Items'}`
            ).replace('{{orderNumber}}', importerOrderNumber ?? '')}
            iconLeft={{ category: 'arrows', name: 'arrow-left' }}
            dataTestId="deliveries-back-button"
            gaComponent="deliveries"
            handleClick={onBack}
          />
        )}
        <StatusBanner
          type="info"
          message={
            labels?.['deliveries.ifChangesAreNeeded'] ??
            'If changes are needed for a delivery, please contact Lucid Support team.'
          }
          dataTestId="deliveries-support-info-banner"
        />
        {successMessage && (
          <Box pt={12}>
            <StatusBanner
              type="success"
              message={successMessage}
              dataTestId="deliveries-pickup-success-banner"
            />
          </Box>
        )}
      </Box>

      {activeDeliveries.length === 0 && (
        <Typography variant="body" size="medium" theme={theme} color="gray-3">
          {labels?.['deliveries.noDeliveries'] ?? 'No deliveries found for this order.'}
        </Typography>
      )}

      {activeDeliveries.map((delivery) => (
        <Box key={delivery.deliveryId} className={styles.accordionSection}>
          <Accordion
            title={renderAccordionTitle(delivery)}
            theme={theme}
            defaultOpen={true}
            dataTestId={`delivery-accordion-${delivery.deliveryNumber}`}
          >
            <Box className={styles.actions}>
              {(() => {
                const commercialInvoiceFile = getDeliveryInvoices(delivery).find(
                  (f) => f.documentCategory === 'COMMERCIAL_INVOICE'
                );
                return (
                  <Button
                    type="primary"
                    size="medium"
                    color="neutral"
                    theme={invertedTheme}
                    label={
                      labels?.['deliveries.downloadCommercialInvoice'] ??
                      'Download Commercial Invoice'
                    }
                    iconLeft={{ category: 'global', name: 'download' }}
                    dataTestId={`download-invoice-${delivery.deliveryId}`}
                    gaComponent="deliveries"
                    handleClick={() =>
                      commercialInvoiceFile
                        ? handleDownloadInvoice(delivery, commercialInvoiceFile)
                        : undefined
                    }
                    disabled={
                      !commercialInvoiceFile ||
                      downloadingFileId === commercialInvoiceFile.sfdcFileIdentifier
                    }
                  />
                );
              })()}
              <Button
                type="primary"
                size="medium"
                color="neutral"
                theme={invertedTheme}
                label={labels?.['deliveries.confirmPickup'] ?? 'Confirm Pickup'}
                iconLeft={{ category: 'global', name: 'circle-checkmark-filled' }}
                dataTestId={`confirm-pickup-${delivery.deliveryId}`}
                gaComponent="deliveries"
                handleClick={() => handleConfirmPickup(delivery)}
                disabled={isUpdating || delivery.status !== 'COMMERCIAL_INVOICE_GENERATED'}
              />
              {(() => {
                const finalInvoiceFile = getDeliveryInvoices(delivery).find(
                  (f) => f.documentCategory === 'FINAL_INVOICE'
                );
                const showFinalInvoice =
                  finalInvoiceFile ||
                  delivery.status === 'PICKUP_CONFIRMED' ||
                  delivery.status === 'CANCELLED';
                if (!showFinalInvoice) return null;
                return (
                  <Button
                    key={`final-invoice-${delivery.deliveryId}`}
                    type="primary"
                    size="medium"
                    color="neutral"
                    theme={invertedTheme}
                    label={labels?.['deliveries.downloadFinalInvoice'] ?? 'Download Final Invoice'}
                    iconLeft={{ category: 'global', name: 'download' }}
                    dataTestId={`download-final-invoice-${delivery.deliveryId}`}
                    gaComponent="deliveries"
                    handleClick={() =>
                      finalInvoiceFile
                        ? handleDownloadInvoice(delivery, finalInvoiceFile)
                        : undefined
                    }
                    disabled={
                      !finalInvoiceFile || downloadingFileId === finalInvoiceFile.sfdcFileIdentifier
                    }
                  />
                );
              })()}
            </Box>

            <TableComponent
              compact
              columns={columns}
              data={delivery.lineItems}
              theme={invertedTheme}
              selection={{ enabled: false }}
              pagination={{
                enabled: true,
                total: delivery.lineItems.length,
                current: 0,
                pageSize: 10,
                onChange: () => {},
                onPageSizeChange: () => {},
              }}
              emptyState={{
                message: labels?.['deliveries.noLineItems'] ?? 'No line items in this delivery.',
              }}
              labels={{
                showingItems:
                  labels?.['table.showingItems'] ?? 'Showing {{start}}-{{end}} of {{total}} Items',
                pageOf: labels?.['table.pageOf'] ?? '{{current}} of {{total}}',
              }}
              gaComponent={`delivery-table-${delivery.deliveryNumber}`}
              dataTestId={`delivery-table-${delivery.deliveryNumber}`}
            />
          </Accordion>
        </Box>
      ))}

      {pickupSliderDelivery && resolvedOrderId && (
        <ConfirmPickupSlider
          delivery={pickupSliderDelivery}
          orderId={resolvedOrderId}
          theme={theme}
          labels={labels}
          onClose={() => setPickupSliderDelivery(null)}
          onSuccess={handlePickupSuccess}
        />
      )}
    </Box>
  );
};

export default Deliveries;
